import 'regenerator-runtime/runtime';
import {DefaultSequenceComponent, SequenceComponent, StartComponent, EndComponent} from "./components";
import Rete from "rete";
import { createRoot } from "react-dom/client";
import ConnectionPlugin from "rete-connection-plugin";
import ReactRenderPlugin from "rete-react-render-plugin";
import ContextMenuPlugin from "rete-context-menu-plugin";
import AreaPlugin from "rete-area-plugin";
import {CustomNodeEditor} from "./extensions/CustomNodeEditor";
import i18next from 'i18next';
import {EditorStorage} from "./editorStorage";
import {SequenceNode} from "./nodes/SequenceNode"
import {ResourceType} from "models/ResourceType";

// todo: props type
const createEditor = async (
    container,
    config,
    onEditorChange,
    onLoaded,
    fetchNodeFileList,
    fetchNodeFileGroups,
    lessonId
) => {
    let components = [
        new SequenceComponent(),
        new DefaultSequenceComponent(),
        new StartComponent(),
        new EndComponent()
    ];

    const originalCreateElement = document.createElement;
  
    document.createElement = function (tagName) {
      const el = originalCreateElement.call(document, tagName);
      if (tagName === 'style') {
        const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content');
        if (nonce) el.setAttribute('nonce', nonce);
      }
      return el;
    };

    let editor = new CustomNodeEditor("iss_node_editor@0.1.0", container);

    editor.storage = new EditorStorage(editor, lessonId);

    editor.use(ConnectionPlugin);
    editor.use(ReactRenderPlugin, {
        component: props => (
            <SequenceNode {...props} />
        ),
        createRoot,
    });
    editor.use(ContextMenuPlugin, {
        delay: 200,
        nodeItems: node => {
            if (node.name === SequenceComponent.COMPONENT_NAME) {
                return {
                    'Delete': false, // disabled because we override Delete with custom implementation (this.customDelete)
                    'Clone': false, // disabled because we override Clone with custom implementation (this.customClone)
                    'Duplicate': () => {
                        customClone(node, editor);
                    },
                    'Remove': () => {
                        customDelete(node, editor);
                    }
                }
            } else if(node.name === DefaultSequenceComponent.COMPONENT_NAME) {
                return {
                    'Delete': false, // disabled because we override Delete with custom implementation (this.customDelete)
                    'Clone': false, // disabled
                    'Remove': () => {
                        customDelete(node, editor);
                    }
                }
            } else if (node.name === StartComponent.COMPONENT_NAME || node.name === EndComponent.COMPONENT_NAME) {
                return {
                    'Delete': false,
                    'Clone': false,
                    'Remove': () => { customDelete(node, editor); }
                }
            } else {
                return {
                    'Delete': false,
                    'Clone': false,
                }
            }
        }
    });
    editor.use(AreaPlugin, {
        scaleExtent: {min: 0.2, max: 1.0}
    });

    let engine = new Rete.Engine("iss_node_editor@0.1.0");

    components.map(c => {
        editor.register(c);
        engine.register(c);
    });

    // editor.on('error', ({message, data}) => {
    //     console.log("editor error msg: ", message);
    //     console.log("editor error data: ", data);
    // });
    // editor.on('connectioncreate', ({output, input}) => { });
    // editor.on('updateconnection', ({ el, connection, points}) => {});
    // editor.on('rendersocket', ({el, input, output, socket}) => {});
    // editor.on('connectionpick', io /* Input or Output */ => {
    //     // if (io instanceof ActionInput) {
    //     //     io.node.connectionDropOnInput();
    //     // }
    // });
    // editor.on('connectiondrop', io /* Input or Output */ => {});
    // editor.on('connectionpath', data => {});
    // editor.on('nodecreated', (node) =>{});

    // editor.on('updateconnection', ({ el, connection, points}) => {
    //     const d = renderPathData(editor, points, connection);
    //     updateConnection({ el, connection, d });
    // });

    editor.on('rendernode', ({el, node, component, bindSocket, bindControl}) => {
        // HACK: FIXME: TODO: there is an issue when we load training, connections has offsets
        // don't know why yet, something is render lately and change size of the whole nodes, pushin sockets lower?
        setTimeout(() => {
            editor.view.updateConnections({node: node});
        }, 100);
    });

    // disable zoom on double click
    editor.on('zoom', ({source}) => {
        return source !== 'dblclick';
    });

    editor.on('nodecreate', (newNode) => {
        if (newNode.name === StartComponent.COMPONENT_NAME) {
            for (let i = 0; i < editor.nodes.length; i++) {
                if (editor.nodes[i].name === StartComponent.COMPONENT_NAME) {
                    alert(i18next.t('onlyOneNodeIsAllowed', {nodeName: StartComponent.COMPONENT_NAME}));
                    console.log('Only one "' + StartComponent.COMPONENT_NAME + '" node is allowed!');
                    return false;
                }
            }
        }

        if (newNode.name === EndComponent.COMPONENT_NAME) {
            for (let i = 0; i < editor.nodes.length; i++) {
                if (editor.nodes[i].name === EndComponent.COMPONENT_NAME) {
                    alert(i18next.t('onlyOneNodeIsAllowed', {nodeName: EndComponent.COMPONENT_NAME}));
                    console.log('Only one "' + EndComponent.COMPONENT_NAME + '" node is allowed!');
                    return false;
                }
            }
        }

        return true;
    });

    editor.on('contextmenu', () => {
        document.querySelectorAll('.context-menu').forEach(el => el.remove());
    });

    // creating custom event, triggered when data is loaded from DB
    editor.bind('dataImported');

    editor.view.resize();
    editor.trigger("process");
    AreaPlugin.zoomAt(editor, editor.nodes);

    // Prevent native text selection when starting a drag on the empty canvas / background.
    // Rete v1 doesn't preventDefault on canvas mousedown, so without this the browser begins
    // a text selection that visibly highlights text inside nodes as the user pans.
    const onCanvasMouseDown = (e) => {
        const target = e.target;
        if (target === container || (target.classList && target.classList.contains('rete-background'))) {
            e.preventDefault();
        }
    };
    container.addEventListener('mousedown', onCanvasMouseDown);
    editor.on('destroy', () => {
        container.removeEventListener('mousedown', onCanvasMouseDown);
    });

    console.log("node editor initialized.");

    if (config && Object.keys(config).length > 0) await editor.fromJSON(config);

    editor.on('process nodecreated noderemoved nodedraged connectioncreate connectionremoved', () => {
        onEditorChange();
    });

    // todo await or not?

    await fetchNodeFileList(editor, ResourceType.NodeVideo);
    await fetchNodeFileGroups(editor, ResourceType.NodeVideo);

    await fetchNodeFileList(editor, ResourceType.NodeImage)
    await fetchNodeFileGroups(editor, ResourceType.NodeImage);

    await fetchNodeFileList(editor, ResourceType.NodeAudio)
    await fetchNodeFileGroups(editor, ResourceType.NodeAudio);

    await fetchNodeFileList(editor, ResourceType.NodeInfoIcon)
    await fetchNodeFileGroups(editor, ResourceType.NodeInfoIcon);

    await fetchNodeFileList(editor, ResourceType.AvatarAnimation)
    await fetchNodeFileGroups(editor, ResourceType.AvatarAnimation);

    onLoaded(editor);
}

const customClone = async (node, editor) => {
    const {name, position: [x, y]} = node;
    const data = JSON.parse(JSON.stringify(node.data));
    const component = editor.components.get(name);
    const newNode = new Rete.Node(name);
    newNode.data = data;
    // @ts-ignore
    newNode.isCloned = true;

    await component.build(newNode);

    newNode.meta = Object.assign({}, newNode.meta);
    newNode.position[0] = x + 10;
    newNode.position[1] = y + 10;

    editor.addNode(newNode);
}

const customDelete = (node, editor) => {
    editor.removeNode(node);
}

export default createEditor;